import { ReactNode, useRef, useState, useEffect, useCallback } from 'react';
import MuiDialog from '@mui/material/Dialog';
import { styled } from '@mui/system';
import { ModalCloseIcon } from '../atoms/icons';
import { Breakpoint } from '@mui/material';
import { BodyTitle, LinkText } from '../atoms/Text';
import { PrintIcon } from '../../icons/analyzation';
import { useReactToPrint } from 'react-to-print';
//import { SelectInput } from './Select';
import { AnalyzationStatus } from '@sleepwell_new_platform/common-functions/lib/common';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from "axios";
import { SleepwellApi } from '@sleepwell_new_platform/common-functions/lib/api';
import { PdfProgress } from '../atoms/Progress';
import { formatDateToYYYYMMDDHHMMSS } from '../../utils/Moment';
const Api = new SleepwellApi(axios);

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number | string;
  maxWidth?: false | Breakpoint | undefined;
  isShowPrintButton?: boolean;
  fileName?: string;
  isNotMaxWidth?: boolean;
  component?: ReactNode;
  selectAnalyzationList?: Array<any>;
  fftDownload?: boolean;
  lang?: "ja" | "en";
};

export const Modal = ({ open, onClose, title, width, children, maxWidth }: Props) => {
  return (
    <StyledModal open={open} onClose={onClose} maxWidth={maxWidth ?? 'lg'}>
      <Container width={width}>
        <Title>
          <BodyTitle style={{ fontSize: 24 }}>
            {title}
          </BodyTitle>
          <CloseIcon onClick={onClose} />
        </Title>
        {children}
      </Container>
    </StyledModal>
  );
};

export const EditModal = ({ open, onClose, title, width, children, maxWidth, isShowPrintButton, fileName, component, lang }: Props) => {
  const componentRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  const waitForStableLayout = useCallback(async () => {
    try {
      // フォント読み込み完了待ち
      // @ts-ignore
      if (document.fonts && document.fonts.ready) {
        // @ts-ignore
        await document.fonts.ready;
      }
    } catch (error) {
      // フォント読み込みエラーは無視して続行
      console.warn('Font loading failed:', error);
    }
    // レイアウト安定のため2フレーム待機
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    // 念のためリサイズイベントで再レイアウトを促す
    window.dispatchEvent(new Event('resize'));
  }, []);

  useEffect(() => {
    if (!open) {
      setIsReady(false);
      return;
    }
    let idleTimer: any;
    let maxWaitTimer: any;
    let pollTimer: any;
    let disconnected = false;
    const setupObserver = (targetEl: HTMLElement) => {
      const handleIdle = async () => {
        await waitForStableLayout();
        if (!disconnected) setIsReady(true);
      };
      const observer = new MutationObserver(() => {
        setIsReady(false);
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(handleIdle, 300);
      });
      observer.observe(targetEl, { childList: true, subtree: true });
      idleTimer = setTimeout(handleIdle, 300);
      maxWaitTimer = setTimeout(() => {
        if (!disconnected) setIsReady(true);
      }, 5000);
      return observer;
    };

    const target = componentRef.current as unknown as HTMLElement | null;
    let observer: MutationObserver | null = null;
    if (target) {
      observer = setupObserver(target);
    } else {
      // ref が未設定でもフォールバックで起動できるようにポーリング
      maxWaitTimer = setTimeout(() => {
        if (!disconnected) setIsReady(true);
      }, 5000);
      pollTimer = setInterval(() => {
        const t = componentRef.current as unknown as HTMLElement | null;
        if (t) {
          clearInterval(pollTimer);
          observer = setupObserver(t);
        }
      }, 150);
    }

    return () => {
      disconnected = true;
      if (observer) observer.disconnect();
      if (idleTimer) clearTimeout(idleTimer);
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [open, lang, waitForStableLayout]);

  const handlePrint = useReactToPrint({
    documentTitle: fileName ?? "",
    content: () => componentRef.current,
    // 印刷取得直前にも安定化を保証
    onBeforeGetContent: waitForStableLayout,
  });

  return (
    <StyledModal
      fullWidth={true}
      maxWidth={maxWidth ?? 'xl'}
      style={{
        width: '100%',
      }}
      open={open} onClose={onClose}>
      <Container width={width}>
        <Title>
          <div style={{ display: 'flex', alignItems: "stretch" }}>
            <BodyTitle style={{ fontSize: 24, marginRight: 16 }}>{title}</BodyTitle>
            {component}
          </div>
          <FlexWrap>
            {isShowPrintButton && (
              <FlexWrap style={{ marginRight: 18, opacity: isReady ? 1 : 0.5, pointerEvents: isReady ? 'auto' : 'none', cursor: isReady ? 'pointer' : 'default' }} onClick={() => handlePrint()}>
                <PrintIcon />
                <LinkText style={{ marginRight: 8, marginLeft: 8 }} fontSize={18}>{'印刷する'}</LinkText>
              </FlexWrap>
            )}
            <CloseIcon onClick={onClose} />
          </FlexWrap>
        </Title>
        <PrintArea ref={componentRef}>
          {children}
        </PrintArea>
      </Container>
    </StyledModal>
  );
};

export const PdfDownloadModal = ({ open, onClose, width, children, maxWidth, selectAnalyzationList}: Props) => {
  const componentRef = useRef(null);
  // 改ページマーカーの位置を収集する関数
  const collectPageBreakPositions = (rootElement: HTMLElement, scale: number): number[] => {
    const breakPositions: number[] = [];
    
    // rootElementの存在確認
    if (!rootElement) {
      console.warn('Root element is null for page break collection');
      return breakPositions;
    }
    
    // getBoundingClientRectメソッドの存在確認
    if (typeof rootElement.getBoundingClientRect !== 'function') {
      console.warn('Root element does not have getBoundingClientRect method');
      return breakPositions;
    }
    
    try {
      const rootRect = rootElement.getBoundingClientRect();
      const scrollOffset = window.pageYOffset || document.documentElement.scrollTop;

      // 改ページマーカーを検索
      const pageBreaks = rootElement.querySelectorAll('.pdf-page-break');
      pageBreaks.forEach((element) => {
        try {
          if (element && typeof element.getBoundingClientRect === 'function') {
            const rect = element.getBoundingClientRect();
            const y = (rect.top - rootRect.top + scrollOffset) * scale;
            if (y > 0) {
              breakPositions.push(y);
            }
          }
        } catch (error) {
          console.warn('Error processing page break element:', error);
        }
      });

    } catch (error) {
      console.error('Error in collectPageBreakPositions:', error);
    }

    return breakPositions.sort((a, b) => a - b);
  };

  // キャンバスをスライスする関数
  const sliceCanvasAtBreakPoints = (canvas: HTMLCanvasElement, breakPoints: number[]): string[] => {
    const slices: string[] = [];
    const ctx = canvas.getContext('2d');
    if (!ctx) return slices;

    // オフスクリーンキャンバスを作成
    const offscreenCanvas = document.createElement('canvas');
    const offscreenCtx = offscreenCanvas.getContext('2d');
    if (!offscreenCtx) return slices;

    let currentY = 0;
    for (const breakPoint of breakPoints) {
      const sliceHeight = breakPoint - currentY;
      if (sliceHeight > 0) {
        // オフスクリーンキャンバスのサイズを設定
        offscreenCanvas.width = canvas.width;
        offscreenCanvas.height = sliceHeight;

        // キャンバスの一部をオフスクリーンキャンバスに描画
        offscreenCtx.drawImage(
          canvas,
          0, currentY, canvas.width, sliceHeight,
          0, 0, canvas.width, sliceHeight
        );

        // 画像データURLに変換
        const sliceDataUrl = offscreenCanvas.toDataURL('image/jpeg', 0.5);
        slices.push(sliceDataUrl);

        currentY = breakPoint;
      }
    }

    // 最後の部分を処理
    if (currentY < canvas.height) {
      const finalSliceHeight = canvas.height - currentY;
      offscreenCanvas.width = canvas.width;
      offscreenCanvas.height = finalSliceHeight;

      offscreenCtx.drawImage(
        canvas,
        0, currentY, canvas.width, finalSliceHeight,
        0, 0, canvas.width, finalSliceHeight
      );

      const finalSliceDataUrl = offscreenCanvas.toDataURL('image/jpeg', 0.5);
      slices.push(finalSliceDataUrl);
    }

    return slices;
  };

  const pdfDownloadHandler = async (selectedAnalyzationList: Array<any>) => {
    if (!componentRef.current) return;

    const formatDateToyyyymmddhhmmss = formatDateToYYYYMMDDHHMMSS(new Date());
    const pdfDownloadedFolderName =  'pdf_files_' + formatDateToyyyymmddhhmmss;

    // A4縦サイズの設定（ポイント単位）
    const A4_WIDTH = 595.28; // A4幅
    const A4_HEIGHT = 841.89; // A4高さ
    const MARGIN = 24; // 余白24pt
    const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2); // コンテンツ幅
    const CONTENT_HEIGHT = A4_HEIGHT - (MARGIN * 2); // コンテンツ高さ

    // html2canvasで高解像度画像を生成
    const canvas = await html2canvas(componentRef.current, { 
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    // 改ページマーカーの位置を収集
    const breakPoints = componentRef.current ? collectPageBreakPositions(componentRef.current as HTMLElement, 2) : [];
    
    // 改ページマーカーがない場合は自動分割
    let slices: string[];
    if (breakPoints.length > 0) {
      // 改ページマーカーでスライス
      slices = sliceCanvasAtBreakPoints(canvas, breakPoints);
    } else {
      // 自動分割（元のロジック）
      const imageQuality = 0.5;
      const imgData = canvas.toDataURL('image/jpeg', imageQuality);
      const canvasImageWidth = canvas.width;
      const canvasImageHeight = canvas.height;

      // 画像のアスペクト比を保持しながらA4に収めるためのスケール計算
      const scaleX = CONTENT_WIDTH / canvasImageWidth;
      const scaleY = CONTENT_HEIGHT / canvasImageHeight;
      const scale = Math.min(scaleX, scaleY, 1);

      const scaledWidth = canvasImageWidth * scale;
      const scaledHeight = canvasImageHeight * scale;

      // 中央配置のためのオフセット計算
      const offsetX = (A4_WIDTH - scaledWidth) / 2;
      const offsetY = (A4_HEIGHT - scaledHeight) / 2;

      // 1ページあたりの画像高さを計算
      const totalPages = Math.ceil(scaledHeight / CONTENT_HEIGHT);

      const filesData = [];

      for (let i = 0; i < selectedAnalyzationList.length; i++) {
        // 新しいPDFドキュメントを作成（A4縦）
        const pdf = new jsPDF('p', 'pt', [A4_WIDTH, A4_HEIGHT]);

        // 各ページに画像の一部を配置
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }

          // 現在のページに表示する画像の部分を計算
          const sourceY = page * CONTENT_HEIGHT / scale;
          const sourceHeight = Math.min(CONTENT_HEIGHT / scale, canvasImageHeight - sourceY);

          // 画像の一部を切り取って配置
          pdf.addImage(
            imgData, 
            'JPG', 
            offsetX, 
            offsetY + (page * CONTENT_HEIGHT), 
            scaledWidth, 
            scaledHeight
          );
        }


        // PDFをBlobとして取得
        const pdfBlob = pdf.output('blob');
        filesData.push({
          fileName: `${pdfDownloadedFolderName}/` + selectedAnalyzationList[i].edfFileName.replace(".EDF", "") + '_report.pdf',
          data: pdfBlob
        });
      }

      // ファイルのダウンロード処理
      if (selectedAnalyzationList.length > 1) {
        // JSZipインスタンスを作成
        const zip = new JSZip();
        // zip化するファイルを追加
        filesData.forEach((fileData: any) => {
          zip.file(fileData.fileName, fileData.data);
        });

        // zipファイルを生成
        const zipBlob = await zip.generateAsync({ type: 'blob' });

        // zipファイルをダウンロード
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(zipBlob);
        downloadLink.download = `${pdfDownloadedFolderName}.zip`;
        downloadLink.click();
      } else if (selectedAnalyzationList.length === 1) {
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(filesData[0].data);
        downloadLink.download = filesData[0].fileName.split('/').pop() || filesData[0].fileName;
        downloadLink.click();
      }
      return;
    }

    // 改ページマーカーがある場合の処理
    const filesData = [];

    for (let i = 0; i < selectedAnalyzationList.length; i++) {
      // 新しいPDFドキュメントを作成（A4縦）
      const pdf = new jsPDF('p', 'pt', [A4_WIDTH, A4_HEIGHT]);

      // 各スライスをページに配置
      for (let page = 0; page < slices.length; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const slice = slices[page];
        const img = new Image();
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // 画像のアスペクト比を保持しながらページに収める
            const scaleX = CONTENT_WIDTH / img.width;
            const scaleY = CONTENT_HEIGHT / img.height;
            const scale = Math.min(scaleX, scaleY, 1);
            
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            
            const offsetX = (A4_WIDTH - scaledWidth) / 2;
            const offsetY = (A4_HEIGHT - scaledHeight) / 2;

            pdf.addImage(slice, 'JPEG', offsetX, offsetY, scaledWidth, scaledHeight);
            resolve();
          };
          img.onerror = reject;
          img.src = slice;
        });
      }

      // PDFをBlobとして取得
      const pdfBlob = pdf.output('blob');
      filesData.push({
        fileName: `${pdfDownloadedFolderName}/` + selectedAnalyzationList[i].edfFileName.replace(".EDF", "") + '_report.pdf',
        data: pdfBlob
      });
    }

    // ファイルのダウンロード処理
    if (selectedAnalyzationList.length > 1) {
      // JSZipインスタンスを作成
      const zip = new JSZip();
      // zip化するファイルを追加
      filesData.forEach((fileData: any) => {
        zip.file(fileData.fileName, fileData.data);
      });

      // zipファイルを生成
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // zipファイルをダウンロード
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(zipBlob);
      downloadLink.download = `${pdfDownloadedFolderName}.zip`;
      downloadLink.click();
    } else if (selectedAnalyzationList.length === 1) {
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(filesData[0].data);
      downloadLink.download = filesData[0].fileName.split('/').pop() || filesData[0].fileName;
      downloadLink.click();
    }
  };
  setTimeout(async () => {
    if (open && selectAnalyzationList) {
      await pdfDownloadHandler(selectAnalyzationList);
      onClose();
    }
    console.log('処理が完了しました。');
  }, 5000);

  return (
    <StyledModal
      fullWidth={true}
      maxWidth={maxWidth}
      open={open} onClose={onClose}>
      <Container width={width}>
        <PdfProgress message='PDFを生成中です。このままお待ちください' />
        <PrintArea ref={componentRef}>
          {children}
        </PrintArea>
      </Container>
    </StyledModal>
  );
};

export const EditWideModal = ({ open, onClose, title, width, children, maxWidth, isShowPrintButton, fileName, component, lang }: Props) => {
  const componentRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  const waitForStableLayout = useCallback(async () => {
    try {
      // フォント読み込み完了待ち
      // @ts-ignore
      if (document.fonts && document.fonts.ready) {
        // @ts-ignore
        await document.fonts.ready;
      }
    } catch (error) {
      // フォント読み込みエラーは無視して続行
      console.warn('Font loading failed:', error);
    }
    // レイアウト安定のため2フレーム待機
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    // 念のためリサイズイベントで再レイアウトを促す
    window.dispatchEvent(new Event('resize'));
  }, []);

  useEffect(() => {
    if (!open) {
      setIsReady(false);
      return;
    }
    let idleTimer: any;
    let maxWaitTimer: any;
    let pollTimer: any;
    let disconnected = false;
    const setupObserver = (targetEl: HTMLElement) => {
      const handleIdle = async () => {
        await waitForStableLayout();
        if (!disconnected) setIsReady(true);
      };
      const observer = new MutationObserver(() => {
        setIsReady(false);
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(handleIdle, 300);
      });
      observer.observe(targetEl, { childList: true, subtree: true });
      idleTimer = setTimeout(handleIdle, 300);
      maxWaitTimer = setTimeout(() => {
        if (!disconnected) setIsReady(true);
      }, 5000);
      return observer;
    };

    const target = componentRef.current as unknown as HTMLElement | null;
    let observer: MutationObserver | null = null;
    if (target) {
      observer = setupObserver(target);
    } else {
      // ref が未設定でもフォールバックで起動できるようにポーリング
      maxWaitTimer = setTimeout(() => {
        if (!disconnected) setIsReady(true);
      }, 5000);
      pollTimer = setInterval(() => {
        const t = componentRef.current as unknown as HTMLElement | null;
        if (t) {
          clearInterval(pollTimer);
          observer = setupObserver(t);
        }
      }, 150);
    }

    return () => {
      disconnected = true;
      if (observer) observer.disconnect();
      if (idleTimer) clearTimeout(idleTimer);
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [open, lang, waitForStableLayout]);

  const handlePrint = useReactToPrint({
    documentTitle: fileName ?? "",
    content: () => componentRef.current,
    // 印刷取得直前にも安定化を保証
    onBeforeGetContent: waitForStableLayout,
  });
 

  return (
    <StyledModal
      fullWidth={true}
      maxWidth={maxWidth}
      open={open} onClose={onClose}>
      <Container width={width}>
      <Title>
          <div style={{ display: 'flex', alignItems: "stretch" }}>
            <BodyTitle style={{ fontSize: 24, marginRight: 16 }}>{title}</BodyTitle>
            {component}
          </div>
          <FlexWrap>
            {isShowPrintButton && <FlexWrap style={{ marginRight: 18, opacity: isReady ? 1 : 0.5, pointerEvents: isReady ? 'auto' : 'none', cursor: isReady ? 'pointer' : 'default' }} onClick={() => handlePrint()}><PrintIcon /><LinkText style={{ marginRight: 8, marginLeft: 8 }}　fontSize={18}>{'印刷する'}</LinkText></FlexWrap>}
            <CloseIcon onClick={onClose} />
          </FlexWrap>
        </Title>
        <PrintArea ref={componentRef}>
          {children}
        </PrintArea>
      </Container>
    </StyledModal>
  );
};


const StyledModal = styled(MuiDialog)({
  borderRadius: 12,
  width: '100%',
});

const Container = styled('div')<{ width?: string | number }>(({ width }) => ({
  position: 'relative',
  width: width ?? '500px',
  padding: '48px',
}));

const Title = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
});

const TitleText = styled('div')({
  color: '#4B4B4B',
  fontSize: '20px',
  fontWeight: 700,
});


const CloseIcon = styled(ModalCloseIcon)({
  cursor: 'pointer',
});

const FlexWrap = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
});

const PrintArea = styled('div')({
});
